import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useAnalytics } from 'src/common/analytics'
import Section from 'src/components/shared/Section'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Img from 'src/components/shared/Img'
import { Fragment } from 'react'
import useDeviceDetect from 'src/common/hooks/useDeviceDetect'

export const onViewH2Id = 'reviews-section'

const ReviewsSection = () => {
  const router = useRouter()
  const { isMobile } = useDeviceDetect()
  const { track } = useAnalytics()
  const { ref } = useIntersectionObserver({
    threshold: 0.8,
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        { additional_info: { section: onViewH2Id }, event_type: 'track' },
        {},
        'section_view'
      )
    },
  })

  const onClick = (e, index, item) => {
    e.preventDefault()

    const data = {
      click_id: item.title,
      click_position: index,
      click_text: `FDR-Web | ${item.title}`,
      click_type: 'Button Click',
      click_url: item.url,
      list_name: item.category,
      event_action: 'link_click',
      event_type: 'track',
    }

    track?.(data, {}, 'button_click')

    router.push(item.url)
  }

  return (
    <Section>
      <div
        className="flex w-full flex-col items-center gap-8 font-ultramarine text-gray-555 lg:gap-14"
        data-testid="facts-reviews-content"
      >
        <h2
          ref={ref}
          className="w-full text-center text-heading-3.5 font-medium leading-6 text-gray-555 lg:text-left lg:text-heading-3 lg:font-bold lg:leading-[31.2px]"
          data-testid="facts-reviews-title"
        >
          Freedom Debt Relief Reviews
        </h2>
        <div data-testid="facts-reviews-list">
          {NEWS.map((item, i) => (
            <Fragment key={item.title}>
              {i > 0 && <div className="my-8 h-px w-full bg-[#C0CBD890]" />}
              <Link
                href={item.url}
                className="group"
                prefetch={false}
                onClick={(e) => onClick(e, i, item)}
                data-testid="facts-reviews-item-link"
              >
                <button className="flex gap-8" data-testid="facts-reviews-item">
                  <div data-testid="facts-reviews-item-media">
                    <div className="size-[85px] lg:h-27 flex items-center justify-center bg-[#F5F9FF] lg:w-48">
                      <Img
                        src={item.imageURL}
                        alt={item.title}
                        layout="intrinsic"
                        width={
                          item.size === 'large'
                            ? isMobile
                              ? 63
                              : 130
                            : isMobile
                            ? 53
                            : 100
                        }
                        height={isMobile ? 80 : 120}
                        imgClassName="object-cover"
                      />
                    </div>
                  </div>
                  <div
                    className="flex flex-col items-start gap-2"
                    data-testid="facts-reviews-item-copy"
                  >
                    <p
                      className="text-left text-14 font-bold uppercase leading-[16.8px] tracking-[0.2em] text-svg-blue"
                      data-testid="facts-reviews-item-category"
                    >
                      {item.category}
                    </p>
                    <p
                      className="text-left text-14 font-medium leading-[16.8px] text-[#171D26] group-hover:underline lg:text-23 lg:leading-[32.2px]"
                      data-testid="facts-reviews-item-title"
                    >
                      {item.title}
                    </p>
                    <p
                      className="hidden text-left text-[15px] font-normal leading-[25.5px] text-[#171D26] lg:block"
                      data-testid="facts-reviews-item-description"
                    >
                      {item.description}
                    </p>
                  </div>
                </button>
              </Link>
            </Fragment>
          ))}
        </div>
      </div>
    </Section>
  )
}

export default ReviewsSection

const NEWS = [
  {
    category: 'Reviews',
    title: 'Los Angeles Times: #1 debt relief company',
    description:
      '“Freedom Debt Relief is the largest debt settlement company in the U.S. It has a track record of resolving billions in consumer debt over more than two decades... This company may be the ideal choice for consumers carrying above-average unsecured debt (over $7,500) and needing a reliable, established solution.”',
    imageURL: '/next-assets/facts/reviews/la-times.png',
    url: 'https://www.latimes.com/b2b/business-partnerships/story/2025-06-12/best-debt-relief-companies-2025',
    size: 'small',
  },
  {
    category: 'Reviews',
    title: 'Forbes: Best for customer service and transparency',
    description:
      '“While debt negotiation is its primary service, the company is dedicated to helping clients find the most suitable solution for their financial situation… The company maintains transparency regarding its services and associated costs, with support available via phone or email for any inquiries.”',
    imageURL: '/next-assets/facts/reviews/forbes.png',
    url: 'https://www.forbes.com/advisor/debt-relief/freedom-debt-relief-review/',
    size: 'small',
  },
  {
    category: 'Reviews',
    title: 'Bankrate: Best for clients with large debt balances',
    description:
      '"We\'ve ranked Freedom Debt Relief as the best debt settlement company for consumers with high debt because, unlike some other services with a maximum limit, Freedom will settle debts over $100,000 for qualified clients."',
    imageURL: '/next-assets/facts/reviews/bankrate.png',
    url: 'https://www.bankrate.com/personal-finance/debt/reviews/freedom-debt-relief/',
    size: 'small',
  },
  {
    category: 'Reviews',
    title: 'CBS News: Best for customer satisfaction',
    description:
      '“Freedom Debt Relief is our selection for the best debt relief service when it comes to customer satisfaction… It has no BBB complaints and also also has an excellent (4.6) rating on Trustpilot with nearly 43,000 reviews (as of June 5, 2024) — indicating that its customers are highly satisfied overall in terms of the outcomes of using this service.”',
    imageURL: '/next-assets/facts/reviews/cbs.png',
    url: 'https://www.cbsnews.com/news/best-debt-relief-companies/',
    size: 'small',
  },
  {
    category: 'Reviews',
    title: 'Business Insider: Best for program guarantee',
    description:
      '"Freedom Debt Relief also offers a Program Guarantee that provides a full refund if the settlement and fees exceed the original amount owed. While it\'s uncommon for a settlement and fees to be more than you originally owed, this type of guarantee is rarely offered by debt relief companies."',
    imageURL: '/next-assets/facts/reviews/insider.png',
    url: 'https://www.businessinsider.com/personal-finance/credit-score/freedom-debt-relief-review',
    size: 'small',
  },
]
